import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import { Download, CheckCircle, Eye, ChevronDown, Plus, ClipboardList, Check, Trash2, Save, X, GripVertical, Eraser } from "lucide-react";
import { FaEdit } from "react-icons/fa";
import { cn } from "@/lib/utils";
import DeleteIngredientModal from "./DeleteIngredientModal";
import ClearAllIngredientsModal from "./ClearAllIngredientsModal";
import { isBeveragePsdRecipe, isBeverageOrBeveragePsdRecipe, isBffProductIngredient } from "../data/ingredientsCalculations";

// ================= 4 HIDDEN COLUMNS DEFINITIONS =================
const HIDDEN_COLUMNS = [
  {
    id: "bffRate",
    label: "BFF Rate (৳)",
    getValue: (vIng, item) => {
      const val = vIng?.bffRate ?? item?.bffRate ?? vIng?.bffRateAtCreation ?? item?.bffRateAtCreation;
      if (val !== undefined && val !== null && val !== "") {
        const num = Number(val);
        return Number.isFinite(num) ? num.toFixed(2) : String(val);
      }
      return "-";
    },
    getTotal: () => "-",
  },
  {
    id: "clientRate",
    label: "Client Rate (৳)",
    getValue: (vIng, item) => {
      const val = vIng?.clientRate ?? item?.clientRate ?? vIng?.clientRateAtCreation ?? item?.clientRateAtCreation;
      if (val !== undefined && val !== null && val !== "") {
        const num = Number(val);
        return Number.isFinite(num) ? num.toFixed(2) : String(val);
      }
      return "-";
    },
    getTotal: () => "-",
  },
  {
    id: "bffCost",
    label: "BFF Cost (৳)",
    getValue: (vIng, item) => {
      const val = vIng?.bffCost ?? item?.bffCost;
      if (val !== undefined && val !== null && val !== "") {
        const num = Number(val);
        return Number.isFinite(num) ? num.toFixed(2) : String(val);
      }
      return "-";
    },
    getTotal: (vTotals) => {
      if (vTotals?.bffCost !== undefined && vTotals?.bffCost !== null && vTotals?.bffCost !== "") {
        const num = Number(vTotals.bffCost);
        return Number.isFinite(num) ? num.toFixed(2) : String(vTotals.bffCost);
      }
      return "-";
    },
  },
  {
    id: "clientCost",
    label: "Client Cost (৳)",
    getValue: (vIng, item) => {
      const val = vIng?.clientCost ?? item?.clientCost;
      if (val !== undefined && val !== null && val !== "") {
        const num = Number(val);
        return Number.isFinite(num) ? num.toFixed(2) : String(val);
      }
      return "-";
    },
    getTotal: (vTotals) => {
      if (vTotals?.clientCost !== undefined && vTotals?.clientCost !== null && vTotals?.clientCost !== "") {
        const num = Number(vTotals.clientCost);
        return Number.isFinite(num) ? num.toFixed(2) : String(vTotals.clientCost);
      }
      return "-";
    },
  },
];

// ================= INGREDIENT TABLE VERSION COLUMN =================
export default function IngredientTable({
  vItem,
  data,
  versionNumStr,
  vIsFinalized,
  onFinalizeVersion,
  onFullDownload,
  onClientDownload,
  showSolidLiquidColumn = true,
  ingredientGroups = [],
  vTotals = {},
  onPrepareSample,
  onSample,
  onEditRow,
  onSaveSpecificFields,
  formatDate = (d) => d || "-",
  isSelectingForCompare = false,
  isSelectedForCompare = false,
  onToggleSelectCompare,
  selectedColumns: externalSelectedColumns,
  onSelectedColumnsChange,
  onClearVersionIngredients,
  recipeFormat = "bakery",
  isBeveragePsd: propIsBeveragePsd,
  tableRef,
  minHeight,
  scaleBatchSize,
}) {
  const navigate = useNavigate();

  const [isClearVersionModalOpen, setIsClearVersionModalOpen] = useState(false);
  const [isClearingVersion, setIsClearingVersion] = useState(false);

  const handleConfirmClearVersion = async () => {
    setIsClearingVersion(true);
    try {
      if (onClearVersionIngredients) {
        await onClearVersionIngredients(vItem || data);
      }
      setIsClearVersionModalOpen(false);
    } catch (err) {
      console.error("Failed to clear version ingredients:", err);
    } finally {
      setIsClearingVersion(false);
    }
  };

  const isBeveragePsd =
    propIsBeveragePsd !== undefined
      ? propIsBeveragePsd
      : isBeveragePsdRecipe(recipeFormat || data?.recipeType || vItem?.recipeType);

  const hideRoleColumn = isBeverageOrBeveragePsdRecipe(recipeFormat || data?.recipeType || vItem?.recipeType);

  const versionId =
    vItem?._id ||
    (data?._id && versionNumStr ? `${data._id}_v${versionNumStr}` : null) ||
    (versionNumStr ? `v_${versionNumStr}` : "default");

  const storageKey = `ingredient_table_cols_${versionId}`;

  // Diluent Volume state for Beverage PSD
  const [isDiluentEditing, setIsDiluentEditing] = useState(false);
  const [isSavingDiluent, setIsSavingDiluent] = useState(false);
  const [draftDiluentName, setDraftDiluentName] = useState(
    vItem?.diluentName || data?.diluentName || "Cold Water"
  );
  const [draftDiluentVolume, setDraftDiluentVolume] = useState(
    vItem?.diluentVolume ?? data?.diluentVolume ?? 100
  );

  useEffect(() => {
    setDraftDiluentName(vItem?.diluentName || data?.diluentName || "Cold Water");
    setDraftDiluentVolume(vItem?.diluentVolume ?? data?.diluentVolume ?? 100);
  }, [vItem?.diluentName, data?.diluentName, vItem?.diluentVolume, data?.diluentVolume]);

  const handleSaveDiluent = async () => {
    try {
      setIsSavingDiluent(true);
      const targetId = vItem?._id || data?._id;
      if (onSaveSpecificFields && targetId) {
        await onSaveSpecificFields(
          {
            diluentName: draftDiluentName,
            diluentVolume: Number(draftDiluentVolume) || 0,
          },
          targetId
        );
      }
      setIsDiluentEditing(false);
    } catch (err) {
      console.error("Failed to save diluent volume:", err);
    } finally {
      setIsSavingDiluent(false);
    }
  };

  const handleCancelDiluent = () => {
    setDraftDiluentName(vItem?.diluentName || data?.diluentName || "Cold Water");
    setDraftDiluentVolume(vItem?.diluentVolume ?? data?.diluentVolume ?? 100);
    setIsDiluentEditing(false);
  };

  // Hidden Columns State (persisted in localStorage)
  const [internalSelectedColumns, setInternalSelectedColumns] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) {
      console.warn("Failed to load selected columns from localStorage", e);
    }
    return [];
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedColumns =
    externalSelectedColumns !== undefined ? externalSelectedColumns : internalSelectedColumns;

  const handleToggleColumn = (colId) => {
    const next = selectedColumns.includes(colId)
      ? selectedColumns.filter((id) => id !== colId)
      : [...selectedColumns, colId];

    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch (e) {
      console.warn("Failed to save selected columns to localStorage", e);
    }

    if (onSelectedColumnsChange) {
      onSelectedColumnsChange(next);
    } else {
      setInternalSelectedColumns(next);
    }
  };

  // Close dropdown on outside click or escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDropdownOpen]);

  // Selected columns to display after S/L (%) in canonical order
  const activeColumns = useMemo(
    () => HIDDEN_COLUMNS.filter((col) => selectedColumns.includes(col.id)),
    [selectedColumns]
  );

  const hiddenCount = HIDDEN_COLUMNS.length - activeColumns.length;

  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);

  const handleSampleClick = () => {
    const versionIngredients =
      Array.isArray(vItem?.ingredients) && vItem.ingredients.length > 0
        ? vItem.ingredients
        : Array.isArray(data?.ingredients) && data.ingredients.length > 0
          ? data.ingredients
          : [];

    const enrichedVItem = {
      ...vItem,
      ingredients: versionIngredients,
    };

    if (onSample) {
      onSample(enrichedVItem, data, scaleBatchSize);
    } else {
      const targetId = vItem?._id || data?._id;
      const targetVersion = vItem?.version ?? 0;
      const queryParams = scaleBatchSize ? `?batchSize=${scaleBatchSize}` : "";
      navigate(`/application-lab/application-recipes/sample/${targetId}/${targetVersion}${queryParams}`, {
        state: { recipe: data, vItem: enrichedVItem, version: targetVersion, batchSize: scaleBatchSize, sampleBatchSize: scaleBatchSize },
      });
    }
  };

  return (
    <div
      ref={tableRef}
      style={minHeight ? { minHeight: `${minHeight}px` } : undefined}
      className="flex flex-col border-b border-[#EEEBF4] dark:border-primary/40 justify-between"
    >
      <div className="flex flex-col">
        {/* Version Header Card (Image 4 & Compare Selection & Diluent Volume) */}
      <div
        className={cn(
          "p-3 lg:p-1.5 xl:p-2 2xl:p-2.5 3xl:p-3 flex flex-col justify-between border-b border-[#EEEBF4] dark:border-primary/40 bg-white dark:bg-[#0D0B14]",
          isBeveragePsd
            ? "h-[224px] lg:h-[120px] xl:h-[155px] 2xl:h-[180px] 3xl:h-[224px]"
            : "h-[148px] lg:h-[80px] xl:h-[105px] 2xl:h-[120px] 3xl:h-[148px]"
        )}
      >
        <div
          onClick={isSelectingForCompare ? onToggleSelectCompare : undefined}
          className={cn(
            "flex flex-col justify-between p-3 lg:p-1.5 xl:p-2 2xl:p-2.5 3xl:p-3 rounded-2xl lg:rounded-lg xl:rounded-xl 2xl:rounded-xl 3xl:rounded-2xl bg-[#F7F5FA] dark:bg-primary/10 border border-[#EEEBF4] dark:border-primary/30 transition-all",
            isSelectingForCompare && "cursor-pointer"
          )}
        >
          {/* Top Row: VERSION XX Badge + Finalize/Approved / Checkbox */}
          <div className="flex items-center justify-between">
            <span className="px-3 lg:px-1.5 xl:px-2 2xl:px-2.5 3xl:px-3 py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 3xl:py-1 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl bg-[#4B208B] text-white font-extrabold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs tracking-wider uppercase">
              VERSION {versionNumStr}
            </span>

            {isSelectingForCompare ? (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSelectCompare?.();
                }}
                className={cn(
                  "w-5 lg:w-3 xl:w-3.5 2xl:w-4 3xl:w-5 h-5 lg:h-3 xl:h-3.5 2xl:h-4 3xl:h-5 rounded-md lg:rounded-[3px] flex items-center justify-center cursor-pointer transition-all",
                  isSelectedForCompare
                    ? "bg-[#4B208B] text-white shadow-xs"
                    : "border-2 border-gray-400 dark:border-gray-500 bg-white/80 dark:bg-black/40 hover:border-[#4B208B]"
                )}
              >
                {isSelectedForCompare && <Check className="w-3.5 lg:w-2 xl:w-2.5 2xl:w-3 3xl:w-3.5 h-3.5 lg:h-2 xl:h-2.5 2xl:h-3 3xl:h-3.5 stroke-[3]" />}
              </div>
            ) : vIsFinalized ? (
              <span className="px-3 lg:px-1.5 xl:px-2 2xl:px-2.5 3xl:px-3 py-[3px] lg:py-[1px] xl:py-[2px] 2xl:py-[2.5px] 3xl:py-[3px] rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl bg-white border border-purple-200 dark:bg-primary/30 text-[#4B208B] dark:text-purple-300 font-bold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs">
                Approved
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onFinalizeVersion?.(vItem)}
                className="px-3 lg:px-1.5 xl:px-2 2xl:px-2.5 3xl:px-3 py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 3xl:py-1 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl bg-[#4B208B] hover:bg-[#3E1B77] text-white font-bold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs flex items-center gap-1.5 lg:gap-1 transition-all shadow-sm cursor-pointer"
              >
                <CheckCircle className="w-3.5 lg:w-2 xl:w-2.5 2xl:w-3 3xl:w-3.5 h-3.5 lg:h-2 xl:h-2.5 2xl:h-3 3xl:h-3.5" />
                Finalize
              </button>
            )}
          </div>

          {/* Middle: Dates and Code */}
          <div className="text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px] leading-tight space-y-0.5 mt-2 lg:mt-1 xl:mt-1.5 2xl:mt-1.5 3xl:mt-2">
            <p className="text-gray-500 font-medium">
              Recipe Creation Date:{" "}
              <span className="font-bold text-gray-800 dark:text-gray-200">
                {formatDate(vItem?.createdAt || data?.createdAt) || "-"}
              </span>
            </p>
            <p className="text-gray-500 font-medium">
              Recipe Code:{" "}
              <span className="font-bold text-gray-800 dark:text-gray-200">
                {vItem?.recipeCode || data?.recipeCode || "-"}
              </span>
              {vIsFinalized && (
                <span className="ml-3 text-gray-500 font-medium">
                  In Development Code:{" "}
                  <span className="font-bold text-gray-800 dark:text-gray-200">
                    {vItem?.inDevelopmentCode ||
                      data?.inDevelopmentCode ||
                      vItem?.referenceCode ||
                      data?.referenceCode ||
                      (vItem?.copiedFromRecipe && typeof vItem.copiedFromRecipe === "object" ? vItem.copiedFromRecipe.recipeCode : null) ||
                      (data?.copiedFromRecipe && typeof data.copiedFromRecipe === "object" ? data.copiedFromRecipe.recipeCode : null) ||
                      "N/A"}
                  </span>
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Download Buttons - Outside the version card (hidden during compare selection) */}
        {!isSelectingForCompare && (
          <div className="flex items-center gap-2 lg:gap-1 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2">
            <button
              type="button"
              onClick={() => onFullDownload?.(vItem)}
              className="flex-1 py-1.5 lg:py-0.5 xl:py-1 2xl:py-1 3xl:py-1.5 px-2 lg:px-1 xl:px-1.5 2xl:px-1.5 3xl:px-2 rounded-full bg-[#4B208B] hover:bg-[#3E1B77] text-white text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px] font-bold flex items-center justify-center gap-1 shadow-sm transition-all cursor-pointer"
            >
              <Download className="w-3 lg:w-1.5 xl:w-2 2xl:w-2.5 3xl:w-3 h-3 lg:h-1.5 xl:h-2 2xl:h-2.5 3xl:h-3" />
              Full Download
            </button>
            <button
              type="button"
              onClick={() => onClientDownload?.(vItem)}
              className="flex-1 py-1.5 lg:py-0.5 xl:py-1 2xl:py-1 3xl:py-1.5 px-2 lg:px-1 xl:px-1.5 2xl:px-1.5 3xl:px-2 rounded-full bg-[#4B208B] hover:bg-[#3E1B77] text-white text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px] font-bold flex items-center justify-center gap-1 shadow-sm transition-all cursor-pointer"
            >
              <Download className="w-3 lg:w-1.5 xl:w-2 2xl:w-2.5 3xl:w-3 h-3 lg:h-1.5 xl:h-2 2xl:h-2.5 3xl:h-3" />
              Client Download
            </button>
          </div>
        )}

        {/* Diluent Volume block (Only for Beverage PSD recipes) */}
        {isBeveragePsd && (
          <div className="pt-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px] font-semibold text-gray-600 dark:text-gray-300">
                Diluent Volume
              </span>
              {!isSelectingForCompare && (
                isDiluentEditing ? (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={handleSaveDiluent}
                      disabled={isSavingDiluent}
                      title="Save Diluent Volume"
                      className="p-1 rounded bg-[#4B208B] hover:bg-[#3E1B77] text-white transition-colors cursor-pointer disabled:opacity-50"
                    >
                      <Save className="w-3.5 lg:w-2 xl:w-2.5 2xl:w-3 3xl:w-3.5 h-3.5 lg:h-2 xl:h-2.5 2xl:h-3 3xl:h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelDiluent}
                      disabled={isSavingDiluent}
                      title="Cancel"
                      className="p-1 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors cursor-pointer"
                    >
                      <X className="w-3.5 lg:w-2 xl:w-2.5 2xl:w-3 3xl:w-3.5 h-3.5 lg:h-2 xl:h-2.5 2xl:h-3 3xl:h-3.5" />
                    </button>
                  </div>
                ) : !vIsFinalized ? (
                  <button
                    type="button"
                    onClick={() => setIsDiluentEditing(true)}
                    title="Edit Diluent Volume"
                    className="p-1 text-gray-500 hover:text-[#4B208B] transition-colors cursor-pointer"
                  >
                    <FaEdit className="w-3 lg:w-1.5 xl:w-2 2xl:w-2.5 3xl:w-3 h-3 lg:h-1.5 xl:h-2 2xl:h-2.5 3xl:h-3" />
                  </button>
                ) : null
              )}
            </div>

            {isDiluentEditing ? (
              <div className="flex items-center rounded-lg border border-[#EEEBF4] dark:border-primary/30 overflow-hidden bg-white dark:bg-[#121019] h-8.5 lg:h-4.5 xl:h-6 2xl:h-7 3xl:h-8.5 divide-x divide-[#EEEBF4] dark:divide-primary/30 shadow-xs">
                <input
                  type="text"
                  value={draftDiluentName}
                  onChange={(e) => setDraftDiluentName(e.target.value)}
                  placeholder="e.g. Cold Water"
                  className="flex-1 px-2.5 lg:px-1.5 xl:px-2 2xl:px-2 3xl:px-2.5 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-medium text-gray-900 dark:text-white bg-transparent outline-none min-w-0"
                />
                <div className="flex items-center gap-1 px-2 w-24 lg:w-14 xl:w-18 2xl:w-20 3xl:w-24 bg-[#F9FAFB] dark:bg-white/[0.02]">
                  <input
                    type="number"
                    value={draftDiluentVolume}
                    onChange={(e) => setDraftDiluentVolume(e.target.value)}
                    className="w-full text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-bold text-gray-900 dark:text-white bg-transparent outline-none text-right"
                  />
                  <span className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-bold text-gray-500 dark:text-gray-400 shrink-0">ml</span>
                </div>
              </div>
            ) : (
              <div className="flex items-center rounded-lg border border-[#EEEBF4] dark:border-primary/30 overflow-hidden bg-[#FCFBFD] dark:bg-[#121019] h-8.5 lg:h-4.5 xl:h-6 2xl:h-7 3xl:h-8.5 divide-x divide-[#EEEBF4] dark:divide-primary/30 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs">
                <div className="flex-1 px-2.5 lg:px-1.5 xl:px-2 2xl:px-2 3xl:px-2.5 py-1 font-medium text-gray-700 dark:text-gray-300 truncate text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px]">
                  {draftDiluentName || "Cold Water"}
                </div>
                <div className="px-2.5 lg:px-1.5 xl:px-2 2xl:px-2 3xl:px-2.5 py-1 font-bold text-gray-900 dark:text-white flex items-center justify-end gap-1 min-w-[65px] lg:min-w-[40px] xl:min-w-[50px] 2xl:min-w-[58px] 3xl:min-w-[65px] bg-[#F9FAFB] dark:bg-white/[0.02] text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px]">
                  <span>{draftDiluentVolume}</span>
                  <span className="font-normal text-gray-500 dark:text-gray-400">ml</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Version Table Columns Header */}
      <div className="flex items-center h-11 lg:h-6 xl:h-7.5 2xl:h-8.5 3xl:h-11 border-b border-[#EEEBF4] dark:border-primary/40 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-semibold text-gray-900 dark:text-white bg-white dark:bg-[#0D0B14]">
        {isBeveragePsd ? (
          <>
            <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 whitespace-nowrap px-1">
              Per Sachet (g/pcs)
            </div>
            <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 whitespace-nowrap px-1">
              Qty (g)
            </div>
            <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 whitespace-nowrap px-1">
              Cmp (%)
            </div>
            <div
              className={cn(
                "flex-1 h-full flex items-center justify-center whitespace-nowrap px-1",
                activeColumns.length > 0 && "border-r border-[#EEEBF4] dark:border-primary/30"
              )}
            >
              Final Dosing (%)
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 whitespace-nowrap px-1">
              Qty (g)
            </div>
            <div
              className={cn(
                "flex-1 h-full flex items-center justify-center whitespace-nowrap px-1",
                (showSolidLiquidColumn || activeColumns.length > 0) &&
                "border-r border-[#EEEBF4] dark:border-primary/30"
              )}
            >
              Cmp (%)
            </div>
          </>
        )}
        {activeColumns.map((col, idx) => (
          <div
            key={col.id}
            className={cn(
              "flex-1 h-full flex items-center justify-center px-1 text-center whitespace-nowrap",
              (idx < activeColumns.length - 1 || (!isBeveragePsd && showSolidLiquidColumn)) &&
              "border-r border-[#EEEBF4] dark:border-primary/30"
            )}
          >
            {col.label}
          </div>
        ))}
        {!isBeveragePsd && showSolidLiquidColumn && (
          <div className="flex-1 h-full flex items-center justify-center whitespace-nowrap px-1">
            S/L (%)
          </div>
        )}
      </div>
      {/* Version Table Data Rows */}
      <div className="flex flex-col">
        {ingredientGroups.map((group, gIdx) => (
          <div
            key={gIdx}
            className="flex"
          >
            {isBeveragePsd ? (
              /* Beverage PSD Columns: Per Sachet, Qty, Cmp, Final Dosing */
              <div
                className={cn(
                  "flex flex-col",
                  activeColumns.length > 0
                    ? "flex-[4] border-r border-[#EEEBF4] dark:border-primary/30"
                    : "flex-1"
                )}
              >
                {group.items.map(({ vIng, index }, itemIdx) => {
                  const isEditable = !vIsFinalized && !isSelectingForCompare;
                  const isHovered = hoveredRowIndex === index;
                  const isLastInGroup = itemIdx === group.items.length - 1;
                  const isSeparator = group.isSeparator && isLastInGroup;
                  return (
                    <div
                      key={index}
                      onMouseEnter={() => setHoveredRowIndex(index)}
                      onMouseLeave={() => setHoveredRowIndex(null)}
                      onClick={() => {
                        if (isEditable) {
                          onEditRow?.(vIng, index, vItem);
                        }
                      }}
                      className={cn(
                        "group/row flex items-center h-12 lg:h-6.5 xl:h-8 2xl:h-9.5 3xl:h-12 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-medium text-gray-800 dark:text-gray-200 hover:bg-[#F7F5FA] dark:hover:bg-primary/10 transition-colors",
                        isSeparator
                          ? "border-b-2 border-[#4B208B]"
                          : "border-b border-[#EEEBF4] dark:border-primary/30",
                        isHovered && "bg-[#F7F5FA] dark:bg-primary/10",
                        isEditable && "cursor-pointer"
                      )}
                    >
                      <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30">
                        {vIng.isMissingInVersion ? "-" : (vIng.perSachet ?? "-")}
                      </div>
                      <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30">
                        {vIng.isMissingInVersion ? "-" : (vIng.displayQuantity ?? vIng.quantity ?? "-")}
                      </div>
                      <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30">
                        {vIng.isMissingInVersion ? "-" : (vIng.composition ?? "-")}
                      </div>
                      <div className="flex-1 h-full flex items-center justify-center gap-1">
                        <span>{vIng.isMissingInVersion ? "-" : (vIng.finalDosing ?? "-")}</span>
                        {isEditable && !vIng.isMissingInVersion && activeColumns.length === 0 && (
                          <span
                            title="Edit Ingredient"
                            className={cn(
                              "text-[#4B208B] hover:text-[#3E1B77] ml-0.5 transition-opacity",
                              isHovered ? "opacity-100" : "opacity-0 group-hover/row:opacity-100"
                            )}
                          >
                            <FaEdit className="w-3 lg:w-1.5 xl:w-2 2xl:w-2.5 3xl:w-3 h-3 lg:h-1.5 xl:h-2 2xl:h-2.5 3xl:h-3" />
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Left columns: Qty (g) and Cmp (%) */
              <div
                className={cn(
                  "flex flex-col",
                  showSolidLiquidColumn || activeColumns.length > 0
                    ? "flex-[2] border-r border-[#EEEBF4] dark:border-primary/30"
                    : "flex-1"
                )}
              >
                {group.items.map(({ vIng, index }, itemIdx) => {
                  const isEditable = !vIsFinalized && !isSelectingForCompare;
                  const isHovered = hoveredRowIndex === index;
                  const isLastInGroup = itemIdx === group.items.length - 1;
                  const isSeparator = group.isSeparator && isLastInGroup;
                  return (
                    <div
                      key={index}
                      onMouseEnter={() => setHoveredRowIndex(index)}
                      onMouseLeave={() => setHoveredRowIndex(null)}
                      onClick={() => {
                        if (isEditable && !vIng.isMissingInVersion) {
                          onEditRow?.(vIng, index, vItem);
                        }
                      }}
                      className={cn(
                        "group/row flex items-center h-12 lg:h-6.5 xl:h-8 2xl:h-9.5 3xl:h-12 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-medium text-gray-800 dark:text-gray-200 hover:bg-[#F7F5FA] dark:hover:bg-primary/10 transition-colors",
                        isSeparator
                          ? "border-b-2 border-[#4B208B]"
                          : "border-b border-[#EEEBF4] dark:border-primary/30",
                        isHovered && "bg-[#F7F5FA] dark:bg-primary/10",
                        isEditable && !vIng.isMissingInVersion && "cursor-pointer"
                      )}
                    >
                      <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30">
                        {vIng.isMissingInVersion ? "-" : (vIng.displayQuantity ?? vIng.quantity ?? "-")}
                      </div>
                      <div className="flex-1 h-full flex items-center justify-center gap-1">
                        <span>{vIng.isMissingInVersion ? "-" : (vIng.composition ?? "-")}</span>
                        {isEditable && !vIng.isMissingInVersion && activeColumns.length === 0 && (
                          <span
                            title="Edit Ingredient"
                            className={cn(
                              "text-[#4B208B] hover:text-[#3E1B77] ml-0.5 transition-opacity",
                              isHovered ? "opacity-100" : "opacity-0 group-hover/row:opacity-100"
                            )}
                          >
                            <FaEdit className="w-3 lg:w-1.5 xl:w-2 2xl:w-2.5 3xl:w-3 h-3 lg:h-1.5 xl:h-2 2xl:h-2.5 3xl:h-3" />
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Unhidden columns: Selected columns displayed before S/L (%) */}
            {activeColumns.length > 0 && (
              <div
                className={cn(
                  "flex flex-col",
                  !isBeveragePsd && showSolidLiquidColumn && "border-r border-[#EEEBF4] dark:border-primary/30"
                )}
                style={{ flex: activeColumns.length }}
              >
                {group.items.map(({ item, vIng, index }, itemIdx) => {
                  const isEditable = !vIsFinalized && !isSelectingForCompare;
                  const isHovered = hoveredRowIndex === index;
                  const isLastInGroup = itemIdx === group.items.length - 1;
                  const isSeparator = group.isSeparator && isLastInGroup;
                  return (
                    <div
                      key={index}
                      onMouseEnter={() => setHoveredRowIndex(index)}
                      onMouseLeave={() => setHoveredRowIndex(null)}
                      onClick={() => {
                        if (isEditable) {
                          onEditRow?.(vIng, index, vItem);
                        }
                      }}
                      className={cn(
                        "group/row flex items-center h-12 lg:h-6.5 xl:h-8 2xl:h-9.5 3xl:h-12 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-medium text-gray-800 dark:text-gray-200 hover:bg-[#F7F5FA] dark:hover:bg-primary/10 transition-colors",
                        isSeparator
                          ? "border-b-2 border-[#4B208B]"
                          : "border-b border-[#EEEBF4] dark:border-primary/30",
                        isHovered && "bg-[#F7F5FA] dark:bg-primary/10",
                        isEditable && "cursor-pointer"
                      )}
                    >
                      {activeColumns.map((col, colIdx) => {
                        const isLastColumnBeforeSL = colIdx === activeColumns.length - 1;
                        return (
                          <div
                            key={col.id}
                            className={cn(
                              "flex-1 h-full flex items-center justify-center px-1 text-center font-medium gap-1",
                              colIdx < activeColumns.length - 1 &&
                              "border-r border-[#EEEBF4] dark:border-primary/30"
                            )}
                          >
                            <span>{vIng.isMissingInVersion ? "-" : col.getValue(vIng, item)}</span>
                            {isEditable && !vIng.isMissingInVersion && isLastColumnBeforeSL && (
                              <span
                                title="Edit Ingredient"
                                className={cn(
                                  "text-[#4B208B] hover:text-[#3E1B77] ml-0.5 transition-opacity",
                                  isHovered ? "opacity-100" : "opacity-0 group-hover/row:opacity-100"
                                )}
                              >
                                <FaEdit className="w-3 lg:w-1.5 xl:w-2 2xl:w-2.5 3xl:w-3 h-3 lg:h-1.5 xl:h-2 2xl:h-2.5 3xl:h-3" />
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Column: S/L (%) spanning the entire group with NO inner dividers (placed AFTER unhidden columns) */}
            {!isBeveragePsd && showSolidLiquidColumn && (
              <div
                className={cn(
                  "flex-1 flex items-center justify-center text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-semibold text-gray-900 dark:text-white bg-white dark:bg-[#0D0B14]",
                  group.isSeparator
                    ? "border-b-2 border-[#4B208B]"
                    : "border-b border-[#EEEBF4] dark:border-primary/30"
                )}
              >
                <span>{group.solidLiquidValue || ""}</span>
              </div>
            )}
          </div>
        ))}

        {/* Dynamic Total Row */}
        <div className="flex items-center h-12 lg:h-6.5 xl:h-8 2xl:h-9.5 3xl:h-12 font-bold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs text-[#4B208B] dark:text-purple-300 border-t-2 border-[#4B208B]/40 bg-[#FCFBFD] dark:bg-[#121019]">
          {isBeveragePsd ? (
            <>
              <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30">
                {vTotals?.perSachet ?? "-"}
              </div>
              <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30">
                {vTotals?.quantity}
              </div>
              <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30">
                {vTotals?.composition}
              </div>
              <div
                className={cn(
                  "flex-1 h-full flex items-center justify-center",
                  activeColumns.length > 0 && "border-r border-[#EEEBF4] dark:border-primary/30"
                )}
              >
                {vTotals?.finalDosing ?? "-"}
              </div>
            </>
          ) : (
            <>
              <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30">
                {vTotals?.quantity}
              </div>
              <div
                className={cn(
                  "flex-1 h-full flex items-center justify-center",
                  (showSolidLiquidColumn || activeColumns.length > 0) &&
                  "border-r border-[#EEEBF4] dark:border-primary/30"
                )}
              >
                {vTotals?.composition}
              </div>
            </>
          )}
          {activeColumns.map((col, idx) => (
            <div
              key={col.id}
              className={cn(
                "flex-1 h-full flex items-center justify-center px-1 text-center",
                (idx < activeColumns.length - 1 || (!isBeveragePsd && showSolidLiquidColumn)) &&
                "border-r border-[#EEEBF4] dark:border-primary/30"
              )}
            >
              {col.getTotal(vTotals)}
            </div>
          ))}
          {!isBeveragePsd && showSolidLiquidColumn && (
            <div className="flex-1 h-full flex items-center justify-center">
              {vTotals?.solidLiquid}
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Action Buttons Row (hidden during compare selection) */}
      {!isSelectingForCompare && (
        <div className="flex flex-col gap-2 lg:gap-1 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 p-4.5 lg:p-2 xl:p-3 2xl:p-3.5 3xl:p-4.5 bg-white dark:bg-[#0D0B14]">
          <div className="flex items-center justify-between gap-1.5">
            {!vIsFinalized && (
              <button
                type="button"
                onClick={() => setIsClearVersionModalOpen(true)}
                className="px-2.5 lg:px-1.5 xl:px-2 2xl:px-2 3xl:px-2.5 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 rounded-[6px] lg:rounded-[4px] border border-rose-200 dark:border-rose-900/40 text-rose-600 dark:text-rose-400 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-semibold bg-rose-50/50 hover:bg-rose-100/70 dark:bg-rose-950/20 dark:hover:bg-rose-950/40 flex items-center gap-1 transition-all cursor-pointer shadow-2xs"
                title={`Clear all ingredient quantities for VERSION ${versionNumStr}`}
              >
                <Eraser className="w-3.5 lg:w-2 xl:w-2.5 2xl:w-3 3xl:w-3.5 h-3.5 lg:h-2 xl:h-2.5 2xl:h-3 3xl:h-3.5" />
                Clear All
              </button>
            )}
            <div className="flex items-center justify-end gap-1.5 ml-auto">
              <button
                type="button"
                onClick={onPrepareSample}
                className="px-2.5 lg:px-1.5 xl:px-2 2xl:px-2 3xl:px-2.5 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 rounded-[6px] lg:rounded-[4px] border border-[#4B208B] text-[#4B208B] dark:text-purple-300 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-semibold bg-[#F9FAFB] hover:bg-[#EFEAF9] dark:hover:bg-primary/25 flex items-center gap-1 transition-all cursor-pointer"
              >
                <ClipboardList className="w-3.5 lg:w-2 xl:w-2.5 2xl:w-3 3xl:w-3.5 h-3.5 lg:h-2 xl:h-2.5 2xl:h-3 3xl:h-3.5" />
                Prepare Sample
              </button>
              <button
                type="button"
                onClick={handleSampleClick}
                className="px-2.5 lg:px-1.5 xl:px-2 2xl:px-2 3xl:px-2.5 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 rounded-[6px] lg:rounded-[4px] bg-primary text-white text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-semibold hover:bg-[#3E1B77] flex items-center gap-1 shadow-sm transition-all cursor-pointer"
              >
                <Plus className="w-3.5 lg:w-2 xl:w-2.5 2xl:w-3 3xl:w-3.5 h-3.5 lg:h-2 xl:h-2.5 2xl:h-3 3xl:h-3.5" />
                Sample
              </button>
            </div>
          </div>
          <div className="flex justify-end relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsDropdownOpen((prev) => !prev)}
              className="px-2 lg:px-1.5 xl:px-2 2xl:px-2 3xl:px-2 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 rounded-[6px] lg:rounded-[4px] border border-gray-200 dark:border-primary/30 bg-white dark:bg-[#121019] text-gray-600 dark:text-gray-300 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-medium flex items-center gap-1 cursor-pointer hover:bg-[#F7F5FA] dark:hover:bg-primary/20 transition-colors"
            >
              <Eye className="w-3.5 lg:w-2 xl:w-2.5 2xl:w-3 3xl:w-3.5 h-3.5 lg:h-2 xl:h-2.5 2xl:h-3 3xl:h-3.5" />
              Hidden ({hiddenCount})
              <ChevronDown
                className={cn(
                  "w-3 lg:w-1.5 xl:w-2 2xl:w-2.5 3xl:w-3 h-3 lg:h-1.5 xl:h-2 2xl:h-2.5 3xl:h-3 transition-transform duration-200",
                  isDropdownOpen && "rotate-180"
                )}
              />
            </button>

            {/* Hidden Columns Dropdown */}
            {isDropdownOpen && (
              <div className="absolute right-0 top-full mt-1.5 w-56 lg:w-40 xl:w-48 2xl:w-52 3xl:w-56 rounded-xl border border-[#EEEBF4] dark:border-primary/40 bg-white dark:bg-[#121019] shadow-xl z-40 py-1.5 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs">
                <div className="px-3 py-1.5 text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-primary/20">
                  Hidden Columns
                </div>
                <div className="py-1 flex flex-col">
                  {HIDDEN_COLUMNS.map((col) => {
                    const isSelected = selectedColumns.includes(col.id);
                    return (
                      <button
                        key={col.id}
                        type="button"
                        onClick={() => handleToggleColumn(col.id)}
                        className="w-full px-3 py-2 text-left flex items-center justify-between hover:bg-[#F7F5FA] dark:hover:bg-primary/15 transition-colors cursor-pointer text-gray-700 dark:text-gray-200"
                      >
                        <span className="font-medium text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs">{col.label}</span>
                        <div
                          className={cn(
                            "w-4 lg:w-2.5 xl:w-3 2xl:w-3.5 3xl:w-4 h-4 lg:h-2.5 xl:h-3 2xl:h-3.5 3xl:h-4 rounded-[4px] flex items-center justify-center transition-all",
                            isSelected
                              ? "bg-[#4B208B] text-white"
                              : "border border-gray-300 dark:border-gray-600 bg-white dark:bg-black/40"
                          )}
                        >
                          {isSelected && <Check className="w-3 lg:w-1.5 xl:w-2 2xl:w-2.5 3xl:w-3 h-3 lg:h-1.5 xl:h-2 2xl:h-2.5 3xl:h-3 stroke-[3]" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Clear Version Ingredients Confirmation Modal */}
      <ClearAllIngredientsModal
        open={isClearVersionModalOpen}
        onOpenChange={setIsClearVersionModalOpen}
        versionName={`VERSION ${versionNumStr}`}
        onConfirm={handleConfirmClearVersion}
        isLoading={isClearingVersion}
      />
    </div>
  );
}

// ================= INGREDIENT TABLE LEFT COLUMN =================
export function IngredientTableLeftHeader({
  globalIngredients = [],
  isConfectionary = false,
  isBeveragePsd = false,
  recipeFormat = "bakery",
  data,
  vItem,
  hideRoleColumn: propHideRoleColumn,
  onOpenBFFModal,
  onOpenStandardIngredientModal,
  isSelectingForCompare = false,
  onDeleteIngredient,
  onDeleteRow,
  onReorderIngredients,
  isFinalized = false,
  isLoading = false,
  height,
  className,
}) {
  const hideRoleColumn =
    propHideRoleColumn !== undefined
      ? propHideRoleColumn
      : isBeverageOrBeveragePsdRecipe(recipeFormat || data?.recipeType || vItem?.recipeType || (isBeveragePsd ? "beverage psd" : ""));

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [ingredientToDelete, setIngredientToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Drag and Drop state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSectionValid, setDragOverSectionValid] = useState(true);

  const canDelete = !isFinalized && !isSelectingForCompare;

  const handleDeleteClick = (e, item, index) => {
    e.stopPropagation();
    setIngredientToDelete({ ...item, index: item.originalIndex ?? index });
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async (item) => {
    const target = item || ingredientToDelete;
    if (!target) return;
    setIsDeleting(true);
    try {
      if (onDeleteIngredient) {
        await onDeleteIngredient(target);
      } else if (onDeleteRow) {
        await onDeleteRow(target);
      }
      setIsDeleteModalOpen(false);
      setIngredientToDelete(null);
    } catch (err) {
      console.error("Failed to delete ingredient:", err);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  };

  // Drag and Drop Handlers
  const handleDragStart = (e, index) => {
    if (!canDelete) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (e, targetIndex) => {
    if (!canDelete || draggedIndex === null) return;
    e.preventDefault();

    const isDraggedBff = isBffProductIngredient(globalIngredients[draggedIndex]);
    const isTargetBff = isBffProductIngredient(globalIngredients[targetIndex]);

    const isValidSection = isDraggedBff === isTargetBff;
    setDragOverIndex(targetIndex);
    setDragOverSectionValid(isValidSection);

    if (!isValidSection) {
      e.dataTransfer.dropEffect = "none";
    } else {
      e.dataTransfer.dropEffect = "move";
    }
  };

  const handleDragLeave = (e, targetIndex) => {
    if (dragOverIndex === targetIndex) {
      setDragOverIndex(null);
      setDragOverSectionValid(true);
    }
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (!canDelete || draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      setDragOverSectionValid(true);
      return;
    }

    const isDraggedBff = isBffProductIngredient(globalIngredients[draggedIndex]);
    const isTargetBff = isBffProductIngredient(globalIngredients[targetIndex]);

    // Enforce strict section isolation (Upper: Standard Ingredients, Lower: BFF Products)
    if (isDraggedBff !== isTargetBff) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      setDragOverSectionValid(true);
      return;
    }

    const reorderedGlobal = [...globalIngredients];
    const [moved] = reorderedGlobal.splice(draggedIndex, 1);
    reorderedGlobal.splice(targetIndex, 0, moved);

    const finalWithSl = reorderedGlobal.map((item, idx) => ({
      ...item,
      sl: idx + 1,
      order: idx,
      sortOrder: idx,
    }));
    onReorderIngredients?.(finalWithSl);

    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragOverSectionValid(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragOverSectionValid(true);
  };

  return (
    <div
      style={height ? { height: `${height}px` } : undefined}
      className={cn("flex flex-col border-b border-[#EEEBF4] dark:border-primary/40 justify-between", className)}
    >
      <div>
        <div
          className={cn(
            "p-6 lg:p-3 xl:p-4 2xl:p-5 3xl:p-6 flex items-end border-b border-[#EEEBF4] dark:border-primary/40",
            isBeveragePsd
              ? "h-[224px] lg:h-[120px] xl:h-[155px] 2xl:h-[180px] 3xl:h-[224px]"
              : "h-[148px] lg:h-[80px] xl:h-[105px] 2xl:h-[120px] 3xl:h-[148px]"
          )}
        >
          <h2 className="text-[22px] lg:text-[12px] xl:text-[15px] 2xl:text-[18px] 3xl:text-[22px] font-bold text-gray-900 dark:text-white tracking-tight">
            Ingredient Table
          </h2>
        </div>

        {/* Table Column Headers */}
        <div className="flex items-center h-11 lg:h-6 xl:h-7.5 2xl:h-8.5 3xl:h-11 border-b border-[#EEEBF4] dark:border-primary/40 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-semibold text-[#0D111A] dark:text-white bg-white dark:bg-[#0D0B14]">
          <div className="w-14 lg:w-7 xl:w-9 2xl:w-11 3xl:w-14 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30">SL</div>
          {!hideRoleColumn && (
            <div className="w-20 lg:w-10 xl:w-13 2xl:w-16 3xl:w-20 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30">Role</div>
          )}
          <div className="flex-1 h-full flex items-center pl-4 lg:pl-2 xl:pl-2.5 2xl:pl-3 3xl:pl-4 text-left">Ingredients</div>
          {isConfectionary && (
            <div className="w-20 lg:w-10 xl:w-13 2xl:w-16 3xl:w-20 h-full flex items-center justify-center border-l border-[#EEEBF4] dark:border-primary/30">
              Process
            </div>
          )}
        </div>

        {/* Left Ingredient Rows */}
        <div className="flex flex-col">
          {globalIngredients.map((item, index) => {
            const isBeingDragged = index === draggedIndex;
            const isTargetOfDrag = index === dragOverIndex;

            return (
              <div
                key={index}
                draggable={canDelete}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={(e) => handleDragLeave(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                className={cn(
                  "group/leftrow flex items-center h-12 lg:h-6.5 xl:h-8 2xl:h-9.5 3xl:h-12 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-medium text-gray-800 dark:text-gray-200 hover:bg-[#F7F5FA] dark:hover:bg-primary/10 transition-all select-none",
                  item.isSeparator
                    ? "border-b-2 border-[#4B208B]"
                    : "border-b border-[#EEEBF4] dark:border-primary/30",
                  canDelete && "cursor-grab active:cursor-grabbing",
                  isBeingDragged && "opacity-40 bg-purple-50 dark:bg-primary/20",
                  isTargetOfDrag && dragOverSectionValid && "bg-[#EFEAF9] dark:bg-primary/30 border-t-2 border-[#4B208B]",
                  isTargetOfDrag && !dragOverSectionValid && "bg-rose-50 dark:bg-rose-950/40 border-t-2 border-rose-500"
                )}
              >
                <div className="w-14 lg:w-7 xl:w-9 2xl:w-11 3xl:w-14 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 gap-0.5">
                  {canDelete && (
                    <GripVertical className="w-3.5 lg:w-2 xl:w-2.5 2xl:w-3 3xl:w-3.5 h-3.5 lg:h-2 xl:h-2.5 2xl:h-3 3xl:h-3.5 text-gray-400 opacity-0 group-hover/leftrow:opacity-100 transition-opacity shrink-0 cursor-grab" />
                  )}
                  <span className="w-6 lg:w-3 xl:w-4 2xl:w-5 3xl:w-6 h-6 lg:h-3 xl:h-4 2xl:h-5 3xl:h-6 rounded-full inline-flex items-center justify-center bg-[#EFEAF9] dark:bg-primary/25 text-[#4B208B] dark:text-purple-300 font-bold text-xs lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-xs shrink-0">
                    {index + 1}
                  </span>
                </div>
                {!hideRoleColumn && (
                  <div className="w-20 lg:w-10 xl:w-13 2xl:w-16 3xl:w-20 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 text-gray-600 dark:text-gray-400 text-center truncate px-1 font-medium">
                    {item.role || "-"}
                  </div>
                )}
                <div className="flex-1 pl-4 lg:pl-2 xl:pl-2.5 2xl:pl-3 3xl:pl-4 pr-3 lg:pr-1.5 xl:pr-2 2xl:pr-2.5 3xl:pr-3 h-full flex items-center justify-between min-w-0">
                  <span className="font-bold text-gray-900 dark:text-white truncate pr-2">
                    {item.name}
                  </span>
                  {canDelete && (
                    <button
                      type="button"
                      title={`Delete ${item.name}`}
                      onClick={(e) => handleDeleteClick(e, item, index)}
                      className="p-1 text-rose-500 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded transition-all opacity-0 group-hover/leftrow:opacity-100 cursor-pointer shrink-0"
                    >
                      <Trash2 className="w-4 lg:w-2 xl:w-2.5 2xl:w-3.5 3xl:w-4 h-4 lg:h-2 xl:h-2.5 2xl:h-3.5 3xl:h-4" />
                    </button>
                  )}
                </div>
                {isConfectionary && (
                  <div className="w-20 lg:w-10 xl:w-13 2xl:w-16 3xl:w-20 h-full flex items-center justify-center border-l border-[#EEEBF4] dark:border-primary/30 text-gray-800 dark:text-gray-200 font-semibold text-center">
                    {item.process || "-"}
                  </div>
                )}
              </div>
            );
          })}

          {/* Total Row */}
          <div className="flex items-center h-12 lg:h-6.5 xl:h-8 2xl:h-9.5 3xl:h-12 font-bold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs text-[#4B208B] dark:text-purple-300 border-t-2 border-[#4B208B]/40 bg-[#FCFBFD] dark:bg-[#121019]">
            <div className="w-14 lg:w-7 xl:w-9 2xl:w-11 3xl:w-14 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30">-</div>
            {!hideRoleColumn && (
              <div className="w-20 lg:w-10 xl:w-13 2xl:w-16 3xl:w-20 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30">-</div>
            )}
            <div className="flex-1 text-center pr-6 lg:pr-3 xl:pr-4 2xl:pr-5 3xl:pr-6">Total</div>
            {isConfectionary && (
              <div className="w-20 lg:w-10 xl:w-13 2xl:w-16 3xl:w-20 h-full flex items-center justify-center border-l border-[#EEEBF4] dark:border-primary/30">
                -
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons Row - matches right side's 2-row layout (hidden during compare selection) */}
      {!isSelectingForCompare && (
        <div className="flex flex-col gap-2 lg:gap-1 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 p-4.5 lg:p-2 xl:p-3 2xl:p-3.5 3xl:p-4.5 bg-white dark:bg-[#0D0B14]">
          <div className="flex items-center">
            <h2 className="text-[18px] lg:text-[10px] xl:text-xs 2xl:text-sm 3xl:text-[18px] font-semibold text-[#0D111A] dark:text-white tracking-tight">
              Add Ingredient
            </h2>
          </div>
          <div className="flex items-center gap-2 lg:gap-1 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2">
            <button
              type="button"
              onClick={onOpenBFFModal}
              className="px-2.5 lg:px-1.5 xl:px-2 2xl:px-2 3xl:px-2.5 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 rounded-[6px] lg:rounded-[4px] border border-[#4B208B] text-primary dark:text-purple-300 font-semibold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs bg-[#EEEBF4] hover:bg-[#EFEAF9] dark:hover:bg-primary/25 transition-all shadow-sm cursor-pointer"
            >
              BFF Product
            </button>
            <button
              type="button"
              onClick={onOpenStandardIngredientModal}
              className="px-2.5 lg:px-1.5 xl:px-2 2xl:px-2 3xl:px-2.5 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 rounded-[6px] lg:rounded-[4px] border border-[#4B208B] text-primary dark:text-purple-300 font-semibold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs bg-[#EEEBF4] hover:bg-[#EFEAF9] dark:hover:bg-primary/25 transition-all shadow-sm cursor-pointer"
            >
              Standard Ingredient
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteIngredientModal
        open={isDeleteModalOpen}
        onOpenChange={(open) => {
          setIsDeleteModalOpen(open);
          if (!open) setIngredientToDelete(null);
        }}
        ingredient={ingredientToDelete}
        ingredientName={ingredientToDelete?.name}
        onConfirm={handleConfirmDelete}
        isLoading={isLoading || isDeleting}
      />
    </div>
  );
}
